import React, {useEffect, useState} from 'react';
import {XMarkIcon} from '@heroicons/react/24/outline';
import styles from './UpdatePolicyPopUp.module.css';
import Button from "../../../components/common/button/Button";
import {getPolicyAPI} from "../../../services/term";
import {updatePolicyAPI} from "../../../services/admin";

interface UpdatePolicyPopUpProps {
    isOpen: boolean;
    onClose: () => void;
}

//개인정보 처리 규정 수정 팝업
const UpdatePolicyPopUp: React.FC<UpdatePolicyPopUpProps> = ({isOpen, onClose}) => {
    const [policy, setPolicy] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [previewMode, setPreviewMode] = useState(false);

    // 테이블 편집을 위한 상태
    const [showTableEditor, setShowTableEditor] = useState(false);
    const [tableData, setTableData] = useState({
        rows: 3,
        cols: 3,
        headers: ['헤더 1', '헤더 2', '헤더 3'],
        cells: [
            ['내용 1', '내용 2', '내용 3'],
            ['내용 4', '내용 5', '내용 6'],
        ]
    });

    const handleSubmit = async () => {
        try {
            //개인정보 처리 규정 수정 API
            await updatePolicyAPI({content: policy});
        } catch (error) {
            console.log('Error update policy request', error);
        }
        fetchPolicy()
        onClose();
        window.location.reload();
    };

    const fetchPolicy = async () => {
        try {
            setLoading(true);
            //개인정보 처리 규정 조회 API
            const response = await getPolicyAPI();
            setPolicy(response);
        } catch (error) {
            console.error('Error fetching policy request:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPolicy();
    }, []);

    // 테이블 삽입 모달 표시
    const openTableEditor = () => {
        setShowTableEditor(true);
    };

    // 테이블 삽입 취소
    const cancelTableEditor = () => {
        setShowTableEditor(false);
    };

    // 테이블 행/열 수 변경
    const updateTableDimensions = (newRows: number, newCols: number) => {
        // 헤더 업데이트
        const newHeaders = [...tableData.headers];
        while (newHeaders.length < newCols) {
            newHeaders.push(`헤더 ${newHeaders.length + 1}`);
        }
        if (newHeaders.length > newCols) {
            newHeaders.splice(newCols);
        }

        // 셀 데이터 업데이트
        const newCells = [...tableData.cells];
        // 행 수 조정
        while (newCells.length < newRows - 1) { // -1은 헤더 행 때문
            const newRow = Array(newCols).fill('').map((_, i) => `내용 ${(newCells.length + 1) * newCols + i + 1}`);
            newCells.push(newRow);
        }
        if (newCells.length > newRows - 1) {
            newCells.splice(newRows - 1);
        }

        // 열 수 조정
        for (let i = 0; i < newCells.length; i++) {
            while (newCells[i].length < newCols) {
                newCells[i].push('');
            }
            if (newCells[i].length > newCols) {
                newCells[i].splice(newCols);
            }
        }

        setTableData({
            rows: newRows,
            cols: newCols,
            headers: newHeaders,
            cells: newCells
        });
    };

    // 헤더 내용 변경
    const updateHeader = (index: number, value: string) => {
        const newHeaders = [...tableData.headers];
        newHeaders[index] = value;
        setTableData({
            ...tableData,
            headers: newHeaders
        });
    };

    // 셀 내용 변경
    const updateCell = (rowIndex: number, colIndex: number, value: string) => {
        const newCells = [...tableData.cells];
        newCells[rowIndex][colIndex] = value;
        setTableData({
            ...tableData,
            cells: newCells
        });
    };

    // 테이블 HTML 생성 및 삽입
    const insertTable = () => {
        const tableHtml = `
<table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
  <thead>
    <tr>
      ${tableData.headers.map(header =>
            `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">${header}</th>`
        ).join('')}
    </tr>
  </thead>
  <tbody>
    ${tableData.cells.map(row =>
            `<tr>
        ${row.map(cell =>
                `<td style="border: 1px solid #ddd; padding: 8px;">${cell}</td>`
            ).join('')}
      </tr>`
        ).join('')}
  </tbody>
</table>
`;

        const textarea = document.getElementById('policy-editor') as HTMLTextAreaElement;
        if (textarea) {
            const cursorPos = textarea.selectionStart;
            const textBefore = policy.substring(0, cursorPos);
            const textAfter = policy.substring(cursorPos);

            setPolicy(textBefore + tableHtml + textAfter);

            // 커서 위치 업데이트
            setTimeout(() => {
                textarea.focus();
                textarea.selectionStart = textarea.selectionEnd = cursorPos + tableHtml.length;
            }, 0);
        } else {
            // 텍스트 영역이 없는 경우 끝에 추가
            setPolicy(policy + tableHtml);
        }

        setShowTableEditor(false);
    };

    if (!isOpen) return null;

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>개인정보 처리 규정 작성</h2>
                    <div className={styles.headerActions}>
                        <Button
                            onClick={() => setPreviewMode(!previewMode)}
                            className={styles.previewToggle}
                            size="small"
                        >
                            {previewMode ? '편집 모드' : '미리보기'}
                        </Button>
                        <button onClick={onClose} className={styles.closeButton}>
                            <XMarkIcon className={styles.closeIcon}/>
                        </button>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.formGroup}>
                        <div className={styles.formHeader}>
                            <label>개인정보 처리 규정</label>
                            {!previewMode && (
                                <div className={styles.editorToolbar}>
                                    <Button
                                        onClick={openTableEditor}
                                        className={styles.toolbarButton}
                                        title="표 삽입"
                                        size="small"
                                    >
                                        표 삽입
                                    </Button>
                                </div>
                            )}
                        </div>
                        {previewMode ? (
                            <div
                                className={styles.preview}
                                dangerouslySetInnerHTML={{ __html: policy }}
                            />
                        ) : (
                            <textarea
                                id="policy-editor"
                                value={policy}
                                onChange={(e) => setPolicy(e.target.value)}
                                placeholder="개인정보 처리 규정을 적어주세요"
                                className={styles.textarea}
                            />
                        )}
                    </div>
                </div>

                <div className={styles.footer}>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!policy?.trim()}
                        className={styles.submitButton}
                    >
                        저장
                    </Button>
                </div>
            </div>

            {/* 테이블 편집 모달 */}
            {showTableEditor && (
                <div className={styles.tableEditorOverlay}>
                    <div className={styles.tableEditorModal}>
                        <div className={styles.tableEditorHeader}>
                            <h3>표 삽입</h3>
                            <button
                                onClick={cancelTableEditor}
                                className={styles.closeButton}
                            >
                                <XMarkIcon className={styles.closeIcon}/>
                            </button>
                        </div>

                        <div className={styles.tableEditorContent}>
                            <div className={styles.tableDimensions}>
                                <div className={styles.dimensionControl}>
                                    <label>행 수:</label>
                                    <input
                                        type="number"
                                        min="2"
                                        max="10"
                                        value={tableData.rows === 0 ? '' : tableData.rows}
                                        onChange={(e) => updateTableDimensions(parseInt(e.target.value), tableData.cols)}
                                        className={styles.dimensionInput}
                                    />
                                </div>
                                <div className={styles.dimensionControl}>
                                    <label>열 수:</label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={tableData.cols === 0 ? '' : tableData.cols}
                                        onChange={(e) => updateTableDimensions(tableData.rows, parseInt(e.target.value))}
                                        className={styles.dimensionInput}
                                    />
                                </div>
                            </div>

                            <div className={styles.tablePreviewWrapper}>
                                <table className={styles.tablePreview}>
                                    <thead>
                                    <tr>
                                        {tableData.headers.map((header, index) => (
                                            <th key={`header-${index}`}>
                                                <input
                                                    type="text"
                                                    value={header}
                                                    onChange={(e) => updateHeader(index, e.target.value)}
                                                    className={styles.tableInput}
                                                />
                                            </th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {tableData.cells.map((row, rowIndex) => (
                                        <tr key={`row-${rowIndex}`}>
                                            {row.map((cell, colIndex) => (
                                                <td key={`cell-${rowIndex}-${colIndex}`}>
                                                    <input
                                                        type="text"
                                                        value={cell}
                                                        onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                                                        className={styles.tableInput}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className={styles.tableEditorFooter}>
                                <Button
                                    variant="primary"
                                    onClick={insertTable}
                                    className={styles.insertTableButton}
                                >
                                    표 삽입
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={cancelTableEditor}
                                    className={styles.cancelButton}
                                >
                                    취소
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UpdatePolicyPopUp;